import type {
  ApiData,
  ArticleCategory,
  ArticleDetailItem,
  ArticleItem,
  ArticleQuery,
  BannerItem,
  BannerQuery,
  CertificateCategory,
  CertificateItem,
  CertificateQuery,
  FeedbackMeta,
  FeedbackSubmission,
  FeedbackSubmitResponse,
  HomepageData,
  MediaCategory,
  MediaItem,
  MediaQuery,
  PageResponse,
  ProductCategory,
  ProductItem,
  ProductQuery,
} from '~/types/site-api'

/** 清理空查询值，并统一去除字符串查询参数首尾空白。 */
function cleanQuery<T extends object>(query: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(query).flatMap(([key, value]) => {
      if (value === undefined || value === null) return []
      if (typeof value === 'string') {
        const trimmed = value.trim()
        return trimmed ? [[key, trimmed]] : []
      }
      return [[key, value]]
    })
  ) as Partial<T>
}

/**
 * 创建不携带后台 JWT 的官网公开接口客户端。
 * @param baseURL - 可选 API 基址，服务端渲染或 Nitro 路由可传入绝对地址
 */
export function useSiteApi(baseURL?: string) {
  const config = useRuntimeConfig()
  const resolvedBaseURL = String(baseURL || config.public.apiBase || '/api').replace(/\/$/, '')
  const request = $fetch.create({
    baseURL: resolvedBaseURL,
    headers: {
      Accept: 'application/json',
      // 静态生成/服务端渲染流量携带构建专用 UA，供后端 site-api 限流器识别放行；
      // Mooeen 系项目共用同一 UA 约定，浏览器端（客户端分页、筛选等）不附加，用户请求保持默认限流。
      ...(import.meta.server ? { 'User-Agent': 'Mooeen-Site-Prerender' } : {}),
    },
    retry: 0,
  })

  return {
    getHomepage: () => request<ApiData<HomepageData>>('/homepage'),
    getBanners: (query: BannerQuery = {}, signal?: AbortSignal) =>
      request<ApiData<BannerItem[]>>('/banners', { query: cleanQuery(query), signal }),
    getArticleCategories: (signal?: AbortSignal) =>
      request<ApiData<ArticleCategory[]>>('/article-categories', { signal }),
    getArticles: (query: ArticleQuery = {}, signal?: AbortSignal) =>
      request<PageResponse<ArticleItem>>('/articles', {
        query: cleanQuery(query),
        signal,
        priority: 'high',
      }),
    getArticle: (id: string) =>
      request<ApiData<ArticleDetailItem>>(`/articles/${encodeURIComponent(id)}`),
    getMediaCategories: (signal?: AbortSignal) =>
      request<ApiData<MediaCategory[]>>('/media-categories', { signal }),
    getMediaItems: (query: MediaQuery = {}, signal?: AbortSignal) =>
      request<PageResponse<MediaItem>>('/media-items', {
        query: cleanQuery(query),
        signal,
        priority: 'high',
      }),
    getMediaItem: (id: string, signal?: AbortSignal) =>
      request<ApiData<MediaItem>>(`/media-items/${encodeURIComponent(id)}`, { signal }),
    getCertificateCategories: () =>
      request<ApiData<CertificateCategory[]>>('/certificate-categories'),
    getCertificates: (query: CertificateQuery = {}, signal?: AbortSignal) =>
      request<PageResponse<CertificateItem>>('/certificates', {
        query: cleanQuery(query),
        signal,
      }),
    getCertificate: (id: string) =>
      request<ApiData<CertificateItem>>(`/certificates/${encodeURIComponent(id)}`),
    getProductCategories: () => request<ApiData<ProductCategory[]>>('/product-categories'),
    getProducts: (query: ProductQuery = {}, signal?: AbortSignal) =>
      request<PageResponse<ProductItem>>('/products', {
        query: cleanQuery(query),
        signal,
        priority: 'high',
      }),
    getProduct: (id: string) =>
      request<ApiData<ProductItem>>(`/products/${encodeURIComponent(id)}`),
    getFeedbackMeta: () => request<FeedbackMeta>('/feedbacks/meta'),
    submitFeedback: (body: FeedbackSubmission) =>
      request<FeedbackSubmitResponse>('/feedbacks', {
        method: 'POST',
        body,
      }),
  }
}
